import React, { useDeferredValue, useState } from "react";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaPlus } from "react-icons/fa";
import adminApi from "services/admin-api";
import { DataTableSkeleton } from "components/admin";
import { formatMoney, isRecoverableUploadResponseError } from "utils/admin";
import { AppLayout } from "components/layouts/app-layout/app-layout";
import { DataTable } from "components/molecules/table/table";
import RightDrawer from "components/global/RightDrawer";
import { MerchandiseEditorForm } from "components/admin/forms";
import { PageHeader, StatusPill } from "components/admin/ui";
import { buildCurrentUserQueryOptions } from "queries/current-user";
import {
  canManageAllMerchandiseItems,
  canManageMerchandiseItems,
} from "utils/portal-access";
import {
  MerchandisePrimaryButton,
  MerchandiseInfoRow,
  MerchandiseGridLayout,
  MerchandiseThumbnailImage,
  MerchandiseFieldInput,
  MerchandiseLabelText,
  MerchandiseMetaText,
  MerchandiseFieldSelect,
} from "pages/app/merchandise/styles/merchandise.page.inline.styles";
const emptyMerchandise = {
  player_id: "",
  name: "",
  description: "",
  long_description: "",
  price: "",
  stock: "",
  category: "jerseys",
  image: "",
  gallery_images: [],
  metadata: {
    signed: true,
    featured: false,
  },
};
const MerchandisePage = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const deferredSearch = useDeferredValue(searchValue);
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["merchandise"],
    queryFn: () =>
      adminApi.merchandise.list({
        sort: "popular",
      }),
  });
  const { data: athletes = [] } = useQuery({
    queryKey: ["athletes"],
    queryFn: () => adminApi.athletes.list(),
  });
  const { data: currentUser } = useQuery(buildCurrentUserQueryOptions());
  const portalAccess = currentUser?.portal_access;
  const canManageAllMerchandise = canManageAllMerchandiseItems(portalAccess);
  const canManageOwnMerchandise =
    canManageMerchandiseItems(portalAccess, {
      hasLinkedPlayer: Boolean(currentUser?.player?.id),
    }) && !canManageAllMerchandise;
  const canManageMerchandise = canManageAllMerchandise || canManageOwnMerchandise;
  const saveMutation = useMutation({
    mutationFn: (payload: any) => {
      if (!canManageMerchandise) {
        return Promise.reject(new Error("You do not have permission to manage merchandise."));
      }

      const normalizedPayload = {
        ...payload,
        price: payload.price ? Number(payload.price) : 0,
        stock: payload.stock === "" ? undefined : Number(payload.stock),
      };
      return selectedItem
        ? adminApi.merchandise.update(selectedItem.id, normalizedPayload)
        : adminApi.merchandise.create(normalizedPayload);
    },
    onSuccess: () => {
      toast.success(selectedItem ? "Merchandise updated." : "Merchandise created.");
      queryClient.invalidateQueries({
        queryKey: ["merchandise"],
      });
      setSelectedItem(null);
      setDrawerOpen(false);
    },
    onError: (error: any, variables: any) => {
      if (isRecoverableUploadResponseError(error, variables)) {
        toast.success("Merchandise saved and image refreshed.");
        queryClient.invalidateQueries({
          queryKey: ["merchandise"],
        });
        setSelectedItem(null);
        setDrawerOpen(false);
        return;
      }
      toast.error(error?.response?.data?.error || "Unable to save this merchandise item.");
    },
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => {
      if (!canManageMerchandise) {
        return Promise.reject(new Error("You do not have permission to delete merchandise."));
      }

      return adminApi.merchandise.remove(id);
    },
    onSuccess: () => {
      toast.success("Merchandise deleted.");
      queryClient.invalidateQueries({
        queryKey: ["merchandise"],
      });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || "Unable to delete this merchandise item.");
    },
  });
  const visibleItems =
    canManageOwnMerchandise && !canManageAllMerchandise
      ? items.filter((item: any) => item.player_id === currentUser?.player?.id)
      : items;
  const categories = Array.from(
    new Set(visibleItems.map((item: any) => item.category).filter(Boolean)),
  ) as string[];
  const filteredItems = visibleItems.filter((item: any) => {
    const matchesSearch = [item.name, item.description, item.player_name, item.team_name]
      .join(" ")
      .toLowerCase()
      .includes(deferredSearch.trim().toLowerCase());
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    const matchesAvailability =
      availabilityFilter === "all" ||
      (availabilityFilter === "in_stock" && item.available) ||
      (availabilityFilter === "out_of_stock" && !item.available);
    return matchesSearch && matchesCategory && matchesAvailability;
  });
  return (
    <AppLayout route="/merchandise">
      <PageHeader
        title="Merchandise"
        description={
          canManageAllMerchandise
            ? "Curate storefront items, stock position, pricing, and athlete-linked merchandise from the platform workspace."
            : canManageOwnMerchandise
            ? "Manage your own storefront items, stock position, pricing, and athlete-linked merchandise."
            : "Browse athlete-linked storefront items, stock position, and pricing exposed by the API."
        }
        actions={
          canManageMerchandise ? (
            <MerchandisePrimaryButton
              onClick={() => {
                setSelectedItem(null);
                setDrawerOpen(true);
              }}
              type="button"
            >
              <FaPlus size={12} />
              Add item
            </MerchandisePrimaryButton>
          ) : null
        }
      />

      <DataTable
        canEdit={canManageMerchandise}
        canRemove={canManageMerchandise}
        columns={[
          {
            label: "Item",
            accessor: (row: any) => (
              <MerchandiseInfoRow>
                <MerchandiseThumbnailImage
                  alt={row.name || "Merchandise item"}
                  fallbackLabel={row.name}
                  src={row.image}
                  variant="merchandise"
                />
                <div>
                  <MerchandiseLabelText>{row.name}</MerchandiseLabelText>
                  <MerchandiseMetaText>
                    {row.team_name || row.player_name || "No linked team yet"}
                  </MerchandiseMetaText>
                </div>
              </MerchandiseInfoRow>
            ),
          },
          {
            label: "Athlete",
            accessor: (row: any) => row.player_name || "Unknown",
          },
          {
            label: "Category",
            accessor: (row: any) => row.category || "Uncategorised",
          },
          {
            label: "Stock",
            accessor: (row: any) => row.stock ?? "Open",
          },
          {
            label: "Price",
            accessor: (row: any) => row.formatted_price || formatMoney(row.price),
          },
          {
            label: "Availability",
            accessor: (row: any) => <StatusPill value={row.available ? "active" : "inactive"} />,
          },
        ]}
        data={filteredItems}
        emptyDescription="Create a merchandise item to begin managing storefront inventory."
        emptyTitle="No merchandise matches these filters"
        isLoading={isLoading}
        loadingState={<DataTableSkeleton columns={6} rows={5} showThumbnail />}
        onEdit={
          canManageMerchandise
            ? (item) => {
                setSelectedItem(item);
                setDrawerOpen(true);
              }
            : undefined
        }
        onRemove={
          canManageMerchandise
            ? (id) => {
                if (window.confirm("Delete this merchandise item? This action cannot be undone.")) {
                  deleteMutation.mutate(Number(id));
                }
              }
            : undefined
        }
        onView={(id) => history.push(`/merchandise/${id}`)}
        toolbar={
          <MerchandiseGridLayout>
            <MerchandiseFieldInput
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder="Search by item, description, athlete, or team"
              value={searchValue}
            />
            <MerchandiseFieldSelect
              onChange={(event) => setCategoryFilter(event.target.value)}
              value={categoryFilter}
            >
              <option value="all">All categories</option>
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </MerchandiseFieldSelect>
            <MerchandiseFieldSelect
              onChange={(event) => setAvailabilityFilter(event.target.value)}
              value={availabilityFilter}
            >
              <option value="all">All availability</option>
              <option value="in_stock">In stock</option>
              <option value="out_of_stock">Out of stock</option>
            </MerchandiseFieldSelect>
          </MerchandiseGridLayout>
        }
      />

      {canManageMerchandise ? (
        <RightDrawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setSelectedItem(null);
            setDrawerOpen(false);
          }}
          title={selectedItem ? "Edit merchandise item" : "Create merchandise item"}
        >
          <MerchandiseEditorForm
            initialValues={
              selectedItem
                ? {
                    player_id: selectedItem.player_id,
                    name: selectedItem.name,
                    description: selectedItem.description || "",
                    long_description:
                      selectedItem.long_description || selectedItem.description || "",
                    price: selectedItem.price ?? "",
                    stock: selectedItem.stock ?? "",
                    category: selectedItem.category || "jerseys",
                    image: selectedItem.image || "",
                    gallery_images:
                      selectedItem.gallery_images || [selectedItem.image].filter(Boolean),
                    metadata: selectedItem.metadata || {
                      signed: true,
                      featured: false,
                    },
                  }
                : {
                    ...emptyMerchandise,
                    player_id:
                      canManageOwnMerchandise && !canManageAllMerchandise
                        ? currentUser?.player?.id || ""
                        : "",
                  }
            }
            isSubmitting={saveMutation.isPending}
            onCancel={() => {
              setSelectedItem(null);
              setDrawerOpen(false);
            }}
            onSubmit={(values) => saveMutation.mutate(values)}
            playerOptions={(canManageOwnMerchandise && !canManageAllMerchandise
              ? athletes.filter((athlete: any) => athlete.id === currentUser?.player?.id)
              : athletes
            ).map((athlete: any) => ({
              label: athlete.full_name,
              value: athlete.id,
            }))}
            submitLabel={selectedItem ? "Save changes" : "Create item"}
          />
        </RightDrawer>
      ) : null}
    </AppLayout>
  );
};
export default MerchandisePage;
